// ─── Self-contained preview translations ────────────────────────────────
// The Scan To preview is the CUSTOMER-FACING kiosk screen, not the dashboard
// UI. Its chrome (footer, popups, order labels…) must render in the *preview*
// language the user picks — which can be any of the rubric language options,
// INCLUDING ar / ru / de that the dashboard itself doesn't ship as locales.
//
// So these strings live here, fully independent of `src/locales` and the global
// i18next instance. `getPreviewT(lang)` returns a tiny translator bound to the
// preview language; unknown keys fall back to English, then to the key itself.

export type PreviewT = (
    key: string,
    params?: Record<string, string | number>
) => string;

const PREVIEW_FALLBACK_LANG = "en";

export const PREVIEW_STRINGS: Record<string, Record<string, string>> = {
    en: {
        ADVERTISING: "ADVERTISING",
        "Previous slide": "Previous slide",
        "Next slide": "Next slide",
        "No active banner": "No active banner",
        "Legal information": "Legal information",
        "Legal notices": "Legal notices",
        "Privacy policy": "Privacy policy",
        "Terms of sale": "Terms of sale",
        "Terms of use": "Terms of use",
        "Follow us": "Follow us",
        "All rights reserved": "All rights reserved",
        Restaurant: "Restaurant",
        "No order": "No order",
        "Scan a table or order QR code to see your order":
            "Scan a table or order QR code to see your order",
        "Order number": "Order number",
        Time: "Time",
        "In preparation": "In preparation",
        Total: "Total",
        "Choose your payment method": "Choose your payment method",
        "Amount to pay": "Amount to pay",
        "Select your payment method": "Select your payment method",
        "Split payment": "Split payment",
        "Share the bill": "Share the bill",
        Next: "Next",
        Validate: "Validate",
        "How to share the bill?": "How to share the bill?",
        "Choose a split option": "Choose a split option",
        Back: "Back",
        OPTIONS: "OPTIONS",
        "Call the server": "Call the server",
        "Custom message": "Custom message",
        "Describe your request...": "Describe your request...",
        "Send request": "Send request",
        "Offers of the day": "Offers of the day",
        "What would you like to do?": "What would you like to do?",
        "My order": "My order",
        Ordered: "Ordered",
        Ready: "Ready",
        Served: "Served",
        "Discovery Menu": "Discovery Menu",
        "Starter + Main + Dessert + Wine included":
            "Starter + Main + Dessert + Wine included",
        "Happy Hour": "Happy Hour",
        "Half-price cocktails 6pm-8pm": "Half-price cocktails 6pm-8pm",
        "Free Dessert": "Free Dessert",
        "For any order over €25": "For any order over €25",
        "Go to slide {{n}}": "Go to slide {{n}}",
    },
    fr: {
        ADVERTISING: "PUBLICITÉ",
        "Previous slide": "Diapositive précédente",
        "Next slide": "Diapositive suivante",
        "No active banner": "Aucune bannière active",
        "Legal information": "Informations légales",
        "Legal notices": "Mentions légales",
        "Privacy policy": "Politique de confidentialité",
        "Terms of sale": "Conditions Générales de Vente",
        "Terms of use": "Conditions Générales d'Utilisation",
        "Follow us": "Suivez-nous",
        "All rights reserved": "Tous droits réservés",
        Restaurant: "Restaurant",
        "No order": "Aucune commande",
        "Scan a table or order QR code to see your order":
            "Scannez un QR code table ou commande pour voir votre commande",
        "Order number": "Numéro de commande",
        Time: "Heure",
        "In preparation": "En préparation",
        Total: "Total",
        "Choose your payment method": "Choisir le mode de paiement",
        "Amount to pay": "Montant à payer",
        "Select your payment method": "Sélectionnez votre mode de paiement",
        "Split payment": "Paiement séparé",
        "Share the bill": "Partager l'addition",
        Next: "Suivant",
        Validate: "Valider",
        "How to share the bill?": "Comment partager l'addition ?",
        "Choose a split option": "Choisissez une option de partage",
        Back: "Retour",
        OPTIONS: "OPTIONS",
        "Call the server": "Appeler le serveur",
        "Custom message": "Message personnalisé",
        "Describe your request...": "Décrivez votre demande...",
        "Send request": "Envoyer la demande",
        "Offers of the day": "Offres du jour",
        "What would you like to do?": "Que souhaitez-vous faire ?",
        "My order": "Ma commande",
        Ordered: "Commandé",
        Ready: "Prêt",
        Served: "Servi",
        "Discovery Menu": "Menu Découverte",
        "Starter + Main + Dessert + Wine included":
            "Entrée + Plat + Dessert + Vin inclus",
        "Happy Hour": "Heure Heureuse",
        "Half-price cocktails 6pm-8pm": "Cocktails à moitié prix 18h-20h",
        "Free Dessert": "Dessert Offert",
        "For any order over €25": "Pour toute commande supérieure à 25€",
        "Go to slide {{n}}": "Aller à la diapositive {{n}}",
    },
    es: {
        ADVERTISING: "PUBLICIDAD",
        "Previous slide": "Diapositiva anterior",
        "Next slide": "Diapositiva siguiente",
        "No active banner": "Sin banner activo",
        "Legal information": "Información legal",
        "Legal notices": "Aviso legal",
        "Privacy policy": "Política de privacidad",
        "Terms of sale": "Condiciones Generales de Venta",
        "Terms of use": "Condiciones Generales de Uso",
        "Follow us": "Síguenos",
        "All rights reserved": "Todos los derechos reservados",
        Restaurant: "Restaurante",
        "No order": "Sin pedido",
        "Scan a table or order QR code to see your order":
            "Escanee un código QR de mesa o pedido para ver su pedido",
        "Order number": "Número de pedido",
        Time: "Hora",
        "In preparation": "En preparación",
        Total: "Total",
        "Choose your payment method": "Elegir el método de pago",
        "Amount to pay": "Monto a pagar",
        "Select your payment method": "Seleccione su método de pago",
        "Split payment": "Pago separado",
        "Share the bill": "Compartir la cuenta",
        Next: "Siguiente",
        Validate: "Validar",
        "How to share the bill?": "¿Cómo compartir la cuenta?",
        "Choose a split option": "Elija una opción de reparto",
        Back: "Atrás",
        OPTIONS: "OPCIONES",
        "Call the server": "Llamar al camarero",
        "Custom message": "Mensaje personalizado",
        "Describe your request...": "Describa su solicitud...",
        "Send request": "Enviar solicitud",
        "Offers of the day": "Ofertas del día",
        "What would you like to do?": "¿Qué desea hacer?",
        "My order": "Mi pedido",
        Ordered: "Pedido",
        Ready: "Listo",
        Served: "Servido",
        "Discovery Menu": "Menú Descubrimiento",
        "Starter + Main + Dessert + Wine included":
            "Entrada + Plato + Postre + Vino incluido",
        "Happy Hour": "Hora Feliz",
        "Half-price cocktails 6pm-8pm": "Cócteles a mitad de precio 18h-20h",
        "Free Dessert": "Postre Gratis",
        "For any order over €25": "Para pedidos superiores a 25€",
        "Go to slide {{n}}": "Ir a la diapositiva {{n}}",
    },
    it: {
        ADVERTISING: "PUBBLICITÀ",
        "Previous slide": "Diapositiva precedente",
        "Next slide": "Diapositiva successiva",
        "No active banner": "Nessun banner attivo",
        "Legal information": "Informazioni legali",
        "Legal notices": "Note legali",
        "Privacy policy": "Informativa sulla privacy",
        "Terms of sale": "Condizioni Generali di Vendita",
        "Terms of use": "Condizioni Generali di Utilizzo",
        "Follow us": "Seguici",
        "All rights reserved": "Tutti i diritti riservati",
        Restaurant: "Ristorante",
        "No order": "Nessun ordine",
        "Scan a table or order QR code to see your order":
            "Scansiona un codice QR del tavolo o dell'ordine per vedere il tuo ordine",
        "Order number": "Numero d'ordine",
        Time: "Ora",
        "In preparation": "In preparazione",
        Total: "Totale",
        "Choose your payment method": "Scegli il metodo di pagamento",
        "Amount to pay": "Importo da pagare",
        "Select your payment method": "Selezionare il metodo di pagamento",
        "Split payment": "Pagamento separato",
        "Share the bill": "Condividere il conto",
        Next: "Avanti",
        Validate: "Convalidare",
        "How to share the bill?": "Come condividere il conto?",
        "Choose a split option": "Scegliere un'opzione di suddivisione",
        Back: "Indietro",
        OPTIONS: "OPZIONI",
        "Call the server": "Chiamare il cameriere",
        "Custom message": "Messaggio personalizzato",
        "Describe your request...": "Descrivi la tua richiesta...",
        "Send request": "Invia richiesta",
        "Offers of the day": "Offerte del giorno",
        "What would you like to do?": "Cosa vorresti fare?",
        "My order": "Il mio ordine",
        Ordered: "Ordinato",
        Ready: "Pronto",
        Served: "Servito",
        "Discovery Menu": "Menu Scoperta",
        "Starter + Main + Dessert + Wine included":
            "Antipasto + Piatto + Dolce + Vino incluso",
        "Happy Hour": "Ora Felice",
        "Half-price cocktails 6pm-8pm": "Cocktail a metà prezzo 18-20",
        "Free Dessert": "Dolce Offerto",
        "For any order over €25": "Per ogni ordine superiore a 25€",
        "Go to slide {{n}}": "Vai alla diapositiva {{n}}",
    },
    pt: {
        ADVERTISING: "PUBLICIDADE",
        "Previous slide": "Slide anterior",
        "Next slide": "Slide seguinte",
        "No active banner": "Nenhum banner ativo",
        "Legal information": "Informações legais",
        "Legal notices": "Avisos legais",
        "Privacy policy": "Política de privacidade",
        "Terms of sale": "Condições Gerais de Venda",
        "Terms of use": "Condições Gerais de Utilização",
        "Follow us": "Siga-nos",
        "All rights reserved": "Todos os direitos reservados",
        Restaurant: "Restaurante",
        "No order": "Sem encomenda",
        "Scan a table or order QR code to see your order":
            "Digitalize um código QR de mesa ou encomenda para ver a sua encomenda",
        "Order number": "Número da encomenda",
        Time: "Hora",
        "In preparation": "Em preparação",
        Total: "Total",
        "Choose your payment method": "Escolha o método de pagamento",
        "Amount to pay": "Montante a pagar",
        "Select your payment method": "Selecione o seu método de pagamento",
        "Split payment": "Pagamento separado",
        "Share the bill": "Partilhar a conta",
        Next: "Seguinte",
        Validate: "Validar",
        "How to share the bill?": "Como partilhar a conta?",
        "Choose a split option": "Escolha uma opção de divisão",
        Back: "Voltar",
        OPTIONS: "OPÇÕES",
        "Call the server": "Chamar o empregado",
        "Custom message": "Mensagem personalizada",
        "Describe your request...": "Descreva o seu pedido...",
        "Send request": "Enviar pedido",
        "Offers of the day": "Ofertas do dia",
        "What would you like to do?": "O que deseja fazer?",
        "My order": "A minha encomenda",
        Ordered: "Encomendado",
        Ready: "Pronto",
        Served: "Servido",
        "Discovery Menu": "Menu Descoberta",
        "Starter + Main + Dessert + Wine included":
            "Entrada + Prato + Sobremesa + Vinho incluído",
        "Happy Hour": "Hora Feliz",
        "Half-price cocktails 6pm-8pm": "Cocktails a metade do preço 18h-20h",
        "Free Dessert": "Sobremesa Grátis",
        "For any order over €25": "Para encomendas superiores a 25€",
        "Go to slide {{n}}": "Ir para o slide {{n}}",
    },
    de: {
        ADVERTISING: "WERBUNG",
        "Previous slide": "Vorherige Folie",
        "Next slide": "Nächste Folie",
        "No active banner": "Kein aktives Banner",
        "Legal information": "Rechtliche Informationen",
        "Legal notices": "Impressum",
        "Privacy policy": "Datenschutzrichtlinie",
        "Terms of sale": "Allgemeine Geschäftsbedingungen",
        "Terms of use": "Nutzungsbedingungen",
        "Follow us": "Folgen Sie uns",
        "All rights reserved": "Alle Rechte vorbehalten",
        Restaurant: "Restaurant",
        "No order": "Keine Bestellung",
        "Scan a table or order QR code to see your order":
            "Scannen Sie einen Tisch- oder Bestell-QR-Code, um Ihre Bestellung zu sehen",
        "Order number": "Bestellnummer",
        Time: "Uhrzeit",
        "In preparation": "In Zubereitung",
        Total: "Gesamt",
        "Choose your payment method": "Zahlungsart wählen",
        "Amount to pay": "Zu zahlender Betrag",
        "Select your payment method": "Wählen Sie Ihre Zahlungsart",
        "Split payment": "Getrennte Zahlung",
        "Share the bill": "Rechnung teilen",
        Next: "Weiter",
        Validate: "Bestätigen",
        "How to share the bill?": "Wie wird die Rechnung geteilt?",
        "Choose a split option": "Wählen Sie eine Aufteilungsoption",
        Back: "Zurück",
        OPTIONS: "OPTIONEN",
        "Call the server": "Kellner rufen",
        "Custom message": "Individuelle Nachricht",
        "Describe your request...": "Beschreiben Sie Ihre Anfrage...",
        "Send request": "Anfrage senden",
        "Offers of the day": "Angebote des Tages",
        "What would you like to do?": "Was möchten Sie tun?",
        "My order": "Meine Bestellung",
        Ordered: "Bestellt",
        Ready: "Fertig",
        Served: "Serviert",
        "Discovery Menu": "Entdeckungsmenü",
        "Starter + Main + Dessert + Wine included":
            "Vorspeise + Hauptgericht + Dessert + Wein inklusive",
        "Happy Hour": "Happy Hour",
        "Half-price cocktails 6pm-8pm": "Cocktails zum halben Preis 18-20 Uhr",
        "Free Dessert": "Gratis Dessert",
        "For any order over €25": "Für jede Bestellung über 25 €",
        "Go to slide {{n}}": "Zur Folie {{n}} gehen",
    },
    ru: {
        ADVERTISING: "РЕКЛАМА",
        "Previous slide": "Предыдущий слайд",
        "Next slide": "Следующий слайд",
        "No active banner": "Нет активного баннера",
        "Legal information": "Юридическая информация",
        "Legal notices": "Правовая информация",
        "Privacy policy": "Политика конфиденциальности",
        "Terms of sale": "Условия продажи",
        "Terms of use": "Условия использования",
        "Follow us": "Подписывайтесь на нас",
        "All rights reserved": "Все права защищены",
        Restaurant: "Ресторан",
        "No order": "Нет заказа",
        "Scan a table or order QR code to see your order":
            "Отсканируйте QR-код стола или заказа, чтобы увидеть свой заказ",
        "Order number": "Номер заказа",
        Time: "Время",
        "In preparation": "Готовится",
        Total: "Итого",
        "Choose your payment method": "Выберите способ оплаты",
        "Amount to pay": "Сумма к оплате",
        "Select your payment method": "Выберите способ оплаты",
        "Split payment": "Раздельная оплата",
        "Share the bill": "Разделить счёт",
        Next: "Далее",
        Validate: "Подтвердить",
        "How to share the bill?": "Как разделить счёт?",
        "Choose a split option": "Выберите вариант разделения",
        Back: "Назад",
        OPTIONS: "ОПЦИИ",
        "Call the server": "Позвать официанта",
        "Custom message": "Своё сообщение",
        "Describe your request...": "Опишите ваш запрос...",
        "Send request": "Отправить запрос",
        "Offers of the day": "Предложения дня",
        "What would you like to do?": "Что вы хотите сделать?",
        "My order": "Мой заказ",
        Ordered: "Заказано",
        Ready: "Готово",
        Served: "Подано",
        "Discovery Menu": "Меню «Открытие»",
        "Starter + Main + Dessert + Wine included":
            "Закуска + основное блюдо + десерт + вино включены",
        "Happy Hour": "Счастливый час",
        "Half-price cocktails 6pm-8pm":
            "Коктейли за полцены с 18:00 до 20:00",
        "Free Dessert": "Бесплатный десерт",
        "For any order over €25": "При заказе от 25 €",
        "Go to slide {{n}}": "Перейти к слайду {{n}}",
    },
    ar: {
        ADVERTISING: "إعلان",
        "Previous slide": "الشريحة السابقة",
        "Next slide": "الشريحة التالية",
        "No active banner": "لا يوجد بانر نشط",
        "Legal information": "معلومات قانونية",
        "Legal notices": "إشعارات قانونية",
        "Privacy policy": "سياسة الخصوصية",
        "Terms of sale": "الشروط العامة للبيع",
        "Terms of use": "شروط الاستخدام",
        "Follow us": "تابعنا",
        "All rights reserved": "جميع الحقوق محفوظة",
        Restaurant: "مطعم",
        "No order": "لا يوجد طلب",
        "Scan a table or order QR code to see your order":
            "امسح رمز QR للطاولة أو الطلب لعرض طلبك",
        "Order number": "رقم الطلب",
        Time: "الوقت",
        "In preparation": "قيد التحضير",
        Total: "المجموع",
        "Choose your payment method": "اختر طريقة الدفع",
        "Amount to pay": "المبلغ المطلوب دفعه",
        "Select your payment method": "اختر طريقة الدفع",
        "Split payment": "دفع منفصل",
        "Share the bill": "تقسيم الفاتورة",
        Next: "التالي",
        Validate: "تأكيد",
        "How to share the bill?": "كيفية تقسيم الفاتورة؟",
        "Choose a split option": "اختر خيار التقسيم",
        Back: "رجوع",
        OPTIONS: "خيارات",
        "Call the server": "استدعاء النادل",
        "Custom message": "رسالة مخصصة",
        "Describe your request...": "صف طلبك...",
        "Send request": "إرسال الطلب",
        "Offers of the day": "عروض اليوم",
        "What would you like to do?": "ماذا تريد أن تفعل؟",
        "My order": "طلبي",
        Ordered: "تم الطلب",
        Ready: "جاهز",
        Served: "تم التقديم",
        "Discovery Menu": "قائمة الاكتشاف",
        "Starter + Main + Dessert + Wine included":
            "مقبلات + طبق رئيسي + حلوى + نبيذ مشمول",
        "Happy Hour": "ساعة سعيدة",
        "Half-price cocktails 6pm-8pm": "كوكتيلات بنصف السعر من 18 إلى 20",
        "Free Dessert": "حلوى مجانية",
        "For any order over €25": "لأي طلب يزيد عن 25 يورو",
        "Go to slide {{n}}": "الانتقال إلى الشريحة {{n}}",
    },
};

// Returns a translator bound to `lang`. Falls back to English, then to the
// raw key, so a missing entry degrades gracefully instead of rendering blank.
export function getPreviewT(lang: string | undefined | null): PreviewT {
    const dict =
        (lang && PREVIEW_STRINGS[lang]) || PREVIEW_STRINGS[PREVIEW_FALLBACK_LANG];
    const fallback = PREVIEW_STRINGS[PREVIEW_FALLBACK_LANG];
    return (key, params) => {
        let value = dict[key] ?? fallback[key] ?? key;
        // Minimal {{var}} interpolation, mirroring i18next's default syntax.
        if (params) {
            for (const name of Object.keys(params)) {
                value = value.replace(
                    new RegExp(`{{\\s*${name}\\s*}}`, "g"),
                    String(params[name])
                );
            }
        }
        return value;
    };
}
